// GenerateInvoice.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { invoicesService } from "@/api/invoices";
import { companyService } from "@/api/companies";
import CompanySelection from "@pages/admin/invoices/CompanySelection";

const GenerateInvoice = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // const { user } = useSelector((state) => state.auth);
    // const userId = user?.id;

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [items, setItems] = useState([]);
    const [tax, setTax] = useState(19); // Default 19% tax
    const [formData, setFormData] = useState({
        companyId: "",
        issueDate: new Date().toISOString().split("T")[0],
        dueDate: "",
        notes: "",
    });

    // Fetch companies
    const { data: companies, isLoading: companiesLoading } = useQuery({
        queryKey: ["companies"],
        queryFn: () => companyService.getCompanies(1, 100),
    });

    const createMutation = useMutation({
        mutationFn: (data) => invoicesService.createInvoice(data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(["invoices"]);
            alert("Facture créée avec succès !");
            navigate(`/admin/factures/${data.id}`);
        },
        onError: (error) => {
            console.error("Create error:", error);
            alert("Erreur lors de la création de la facture");
        },
    });

    useEffect(() => {
        if (formData.companyId && companies) {
            const company = companies.find(c => c.id === formData.companyId);
            setSelectedCompany(company);
        }
    }, [formData.companyId, companies]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];

        if (field === "name" || field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
            newItems[index] = { ...newItems[index], [field]: value };
        }

        // Recalculate item discount value
        if (field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
            const quantity = parseFloat(field === "quantity" ? value : newItems[index].quantity) || 0;
            const unitPrice = parseFloat(field === "unitPrice" ? value : newItems[index].unitPrice) || 0;
            const discountPercentage = parseFloat(field === "discountPercentage" ? value : newItems[index].discountPercentage) || 0;

            const subtotal = quantity * unitPrice;
            const discountValue = subtotal * (discountPercentage / 100);

            newItems[index].subtotal = subtotal;
            newItems[index].discountValue = discountValue;
        }

        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            {
                name: "",
                quantity: 1,
                unitPrice: 0,
                discountPercentage: 0,
                discountValue: 0,
                subtotal: 0,
            }
        ]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const calculateTotals = () => {
        // Calculate subtotal and total discount from all items
        let subtotal = 0;
        let totalDiscount = 0;

        items.forEach(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const discountPercentage = parseFloat(item.discountPercentage) || 0;

            const itemSubtotal = quantity * unitPrice;
            const itemDiscount = itemSubtotal * (discountPercentage / 100);

            subtotal += itemSubtotal;
            totalDiscount += itemDiscount;
        });

        // Calculate tax amount
        const taxAmount = (subtotal - totalDiscount) * (tax / 100);

        // Calculate final total
        const total = (subtotal - totalDiscount) + taxAmount;

        return {
            subtotal,
            discountAmount: totalDiscount,
            taxAmount,
            total,
            taxRate: tax
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!selectedCompany) {
            alert("Veuillez sélectionner une entreprise");
            return;
        }

        if (items.length === 0) {
            alert("Veuillez ajouter au moins un article");
            return;
        }

        const { total, discountAmount, taxAmount } = calculateTotals();
        console.log({ discountAmount, taxAmount, total })

        const invoiceData = {
            // userId: userId,
            companyId: parseInt(formData.companyId),
            tax: taxAmount,
            items: items.map(item => ({
                name: item.name,
                unitPrice: parseFloat(item.unitPrice) || 0,
                quantity: parseFloat(item.quantity) || 0,
                discountPercentage: parseFloat(item.discountPercentage) || 0,
            })),
        };

        createMutation.mutate(invoiceData);
    };

    const { subtotal, discountAmount, taxAmount, total } = calculateTotals();
    return (
        <div className="p-6">

            <TaleDashbaordMainCard className="p-6">
                <div className="mb-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold">Générer une nouvelle facture</h1>
                            <p className="text-gray-600">Créez une facture pour une entreprise</p>
                        </div>
                        <TaleButton onClick={() => navigate("/admin/factures")}>
                            <i className="fas fa-arrow-left mr-2"></i>
                            Retour aux factures
                        </TaleButton>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Company Selection */}
                        <CompanySelection
                            formData={formData}
                            handleInputChange={handleInputChange}
                            companies={companies?.data || companies} // Adjust based on your API response structure
                            companiesLoading={companiesLoading}
                            selectedCompany={selectedCompany}
                            setSelectedCompany={setSelectedCompany}
                        />
                        {/* Invoice Details */}
                        <div>
                            <h2 className="text-xl font-bold mb-4">Détails de la facture</h2>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <TaleInput
                                        label="Date d'émission"
                                        name="issueDate"
                                        type="date"
                                        value={formData.issueDate}
                                        onChange={handleInputChange}
                                        required
                                    />

                                    <TaleInput
                                        label="Date d'échéance (optionnel)"
                                        name="dueDate"
                                        type="date"
                                        value={formData.dueDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Taux de TVA (%)
                                    </label>
                                    <input
                                        type="number"
                                        value={tax}
                                        onChange={(e) => setTax(parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Notes (optionnel)
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="Notes supplémentaires..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Items */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Articles de la facture</h2>
                            <TaleButton type="button" onClick={addItem}>
                                <i className="fas fa-plus mr-2"></i>
                                Ajouter un article
                            </TaleButton>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                <tr className="bg-gray-50">
                                    <th className="border p-3 text-left">Nom de l'article</th>
                                    <th className="border p-3 text-left">Quantité</th>
                                    <th className="border p-3 text-left">Prix unitaire <sup>(DT)</sup></th>
                                    <th className="border p-3 text-left">Remise (%)</th>
                                    <th className="border p-3 text-left">Valeur remise <sup>(DT)</sup></th>
                                    <th className="border p-3 text-left">Sous-total <sup>(DT)</sup></th>
                                    <th className="border p-3 text-left">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => handleItemChange(index, "name", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                placeholder="Nom de l'article"
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                min="1"
                                                step="1"
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="number"
                                                value={item.unitPrice}
                                                onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                min="0"
                                                step="0.01"
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="number"
                                                value={item.discountPercentage}
                                                onChange={(e) => handleItemChange(index, "discountPercentage", e.target.value)}
                                                className="w-full px-2 py-1 border rounded"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={new Intl.NumberFormat('fr-TN', {
                                                    style: 'currency',
                                                    currency: 'EUR'
                                                }).format(item.discountValue || 0)}
                                                className="w-full px-2 py-1 border rounded bg-gray-50"
                                                readOnly
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <input
                                                type="text"
                                                value={new Intl.NumberFormat('fr-TN', {
                                                    style: 'currency',
                                                    currency: 'EUR'
                                                }).format(item.subtotal || 0)}
                                                className="w-full px-2 py-1 border rounded bg-gray-50"
                                                readOnly
                                            />
                                        </td>
                                        <td className="border p-2 text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeItem(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {items.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                <i className="fas fa-file-invoice text-4xl mb-2"></i>
                                <p>Aucun article ajouté</p>
                                <p className="text-sm mt-2">Cliquez sur "Ajouter un article" pour commencer</p>
                            </div>
                        )}
                    </div>

                    {/* Summary and Actions */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                        {/* Summary */}
                        <div className="w-full lg:w-80">
                            <h3 className="text-lg font-bold mb-4">Résumé de la facture</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>Sous-total:</span>
                                    <span>
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'EUR'
                                        }).format(subtotal)}
                                    </span>
                                </div>

                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                        <span>Remise totale:</span>
                                        <span>- {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'EUR'
                                        }).format(discountAmount)}</span>
                                    </div>
                                )}

                                <div className="flex justify-between pt-2 border-t">
                                    <span>Montant après remise:</span>
                                    <span className="font-semibold">
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'EUR'
                                        }).format(Math.max(0, subtotal - discountAmount))}
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span>TVA ({tax}%):</span>
                                    <span>
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'EUR'
                                        }).format(taxAmount)}
                                    </span>
                                </div>

                                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                                    <span>Total TTC:</span>
                                    <span>
                                        {new Intl.NumberFormat('fr-TN', {
                                            style: 'currency',
                                            currency: 'EUR'
                                        }).format(total)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4">
                            <TaleButton
                                type="button"
                                variant="outlined"
                                onClick={() => navigate("/admin/factures")}
                                disabled={createMutation.isPending}
                            >
                                Annuler
                            </TaleButton>
                            <TaleButton
                                type="submit"
                                loading={createMutation.isPending}
                            >
                                <i className="fas fa-file-invoice mr-2"></i>
                                Créer la facture
                            </TaleButton>
                        </div>
                    </div>
                </form>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default GenerateInvoice;
